import { DatePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit
} from "@angular/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { Subscription, debounceTime, firstValueFrom, tap } from "rxjs";
import { AppState, RoleMgr } from "../../core";
import { UserService } from "../../core/services";
import {
  StatMetric,
  TableColumn,
  TableViewComponentBase,
  TableViewQueryRequest,
  TableViewQueryResponse
} from "../../shared/components/table-view/table-view-component-base";
import { VolunteerHandsetRec, VolunteerHandsetViewRec } from "../models/volunteer-handset";
import { VolunteerTaskViewService } from "../services/volunteer-task-view.service";
import { VolunteerTaskService } from "../services/volunteer-task.service";
import { VolunteerService } from "../services/volunteer.service";
import { AppConfig } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { VolunteerEditComponent } from "../volunteer-edit/volunteer-edit.component";

@Component({
  selector: "app-volunteer-handsets-table",
  templateUrl: "../../shared/components/table-view/table-view.component.html",
  styleUrls: [
    "../../shared/components/table-view/scss/table-view.scss",
    "../../shared/components/table-view/scss/app-tab-full-height.scss",
    "../../shared/components/table-view/scss/fixed-headers-and-column.scss",
    "../volunteer-list/volunteer-list.component.scss",
    "../../post-processing/handset-table/handset-table-cell.component.scss",
  ],
})
export class VolunteerHandsetsTableComponent
  extends TableViewComponentBase<VolunteerHandsetViewRec>
  implements OnInit, OnDestroy
{

  filterKeyUpSubscription: Subscription;
  handsetViewUrlPath = `${AppConfig.apiUrl}/volunteerHandsetView`;

  constructor(
    appState: AppState,
    modalService: BsModalService,
    public volunteerService: VolunteerService,
    public volunteerTaskService: VolunteerTaskService,
    public volunteerTaskViewService: VolunteerTaskViewService,
    elementRef: ElementRef,
    datePipe: DatePipe,
    public roleMgr: RoleMgr,
    public userService: UserService,
    public http: HttpClient,

  ) {
    super(appState, modalService, elementRef, datePipe);

    const idColumn: TableColumn = { title: "ID", name: "id", visible: false };

    this.tableColumns = [
      idColumn,
      { title: "User ID", name: "volunteerId", visible: true},
      { title: "User Name", name: "userName", visible: true},
      { title: "Carrier (OneSource)", name: "carrier", visible: true},
      { title: "Make", name: "make", visible: true},
      { title: "Model", name: "model", visible: true},
      { title: "OS", name: "os", visible: true},
      { title: "Number", name: "number", visible: true},
      { title: "IMEI", name: "imei", visible: true},
      { title: "Device ID", name: "deviceId", visible: true},
      { title: "Bluetooth MAC", name: "btMac", visible: false},

      { title: "User Email", name: "userEmail", visible: false},
      { title: "User City", name: "userCity", visible: true},
      { title: "User State", name: "userState", visible: false},
      { title: "User Status", name: "userStatus", visible: true},
      { title: "User Building", name: "volunteerBuildingId", visible: true},

      { title: "Modified (local)", name: "modifiedAt", template: "localtime", visible: true },
    ];

    this.sortColumns = [
      //{ key: 'id', order: 'ASC'},
      { key: 'volunteerId', order: 'ASC'},
    ];

    this.idColumns = [idColumn];
    this.pageSize = 40; // no paging if null

    const metrics: StatMetric[] = [
      { column: 'modifiedAt', statTypes: ['min','max'], format: { dataType: 'date', layout: 'short' } },
      { column: '*', format: { dataType: 'number' }, statTypes: ['count']}
    ];
    this.statGroups = [
      {
        id: 'os',
        groupBy: ['os'],
        visible: true,
        metrics,
      },
      {
        id: 'carrier',
        groupBy: ['carrier'],
        metrics,
      },
      {
        id: 'make',
        groupBy: ['make'],
        metrics,
      },
      {
        id: 'userCity',
        groupBy: ['userCity'],
        metrics,
      },
      {
        id: 'userState',
        groupBy: ['userState'],
        metrics,
      },
      {
        id: 'userStatus',
        groupBy: ['userStatus'],
        metrics,
      },
      {
        id: 'volunteerBuildingId',
        groupBy: ['volunteerBuildingId'],
        metrics,
      },
    ];
  }

  doQuery = async (queryReq: TableViewQueryRequest) => {
    try {
      const urlPath = `${this.handsetViewUrlPath}/find`;
      const result: any = await firstValueFrom(
        this.http.get(urlPath, {
          params: { criteria: JSON.stringify(queryReq) },
        })
      );
      const resp: TableViewQueryResponse<VolunteerHandsetViewRec> = result.data;
      return resp;
    } catch (error) {
      console.error("find() error: " + error?.message);
      throw error;
    }
  }

  initKeyUpSubscription = () => {
    this.filterKeyUpSubscription?.unsubscribe();
    this.filterKeyUpSubscription = this.filterKeyUp$
      .pipe(
        tap((e) => e.event.key == "Enter" && this.initKeyUpSubscription()), // reset if enter force load already
        debounceTime(700)
      )
      .subscribe(() => { this.load().catch(this.publishError)} );
  };

  ngOnInit(): void {
    this.initKeyUpSubscription();
    this.init();
  }

  ngOnDestroy(): void {
    this.filterKeyUpSubscription?.unsubscribe();
    this.destroy();
  }

  editVolunteer = async (rec: VolunteerHandsetViewRec) => {
    const volunteer = await this.volunteerService.getByIdEagerly(rec.volunteerId);
    const modal = VolunteerEditComponent.showModal(this.modalService,volunteer);
    let updatedHandset: VolunteerHandsetRec = null;
    const handsetSubscription = modal.content.handsetChanged.subscribe({
      next: (handsetRec) => updatedHandset = handsetRec
    })
    await firstValueFrom(modal.onHidden);
    handsetSubscription.unsubscribe();
    if ( !modal.content?.cancelled || updatedHandset) {
      this.load().catch(e=>this.volunteerService.publishError);
    }
  }

  onTabSelected = (bSelected: boolean) => {
    console.log("volunteer-handsets tab selected: " + bSelected);
    if (bSelected) {
      if (!this.bInitialized) {
        this.initialize();
      } else {
        this.load().catch(this.publishError);
      }
    }
  };

  publishError = (e:any)=>this.volunteerService.publishError(e,this,2*60);

  getName = () => "volunteer-handsets-table";
}
